import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CheckCircle, ArrowRight, Clock, Shield, ArrowSquareOut, Lightning, Phone } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";

const US_LAWSHIELD_URL = "https://lp.uslawshield.com/new-member-sign-up-free-trial/?affid=079ebe86-380f-11f1-b538-06ed5a46dc49";
const US_LAWSHIELD_BANNER = "https://www.uslawshield.com/media/us/30DaysFree/banners/tdfbanner468x60.jpg";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const { clear } = useCart();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const attempts = useRef(0);
  const celebrated = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          // Fire confetti once
          if (!celebrated.current) {
            celebrated.current = true;
            // Meta Pixel: fire the "Purchase" event exactly once when payment confirms.
            // Uses amount_total from Stripe (cents → dollars) so campaigns optimize on real revenue.
            if (typeof window !== "undefined" && typeof window.fbq === "function") {
              const value = data.amount_total ? Number(data.amount_total) / 100 : 0;
              window.fbq("track", "Purchase", {
                value,
                currency: (data.currency || "usd").toUpperCase(),
                content_type: "product",
              });
            }
            try {
              confetti({ particleCount: 80, spread: 70, origin: { y: 0.4 }, colors: ["#FFD300", "#FFE149", "#FFFFFF"] });
              setTimeout(() => confetti({ particleCount: 40, spread: 100, origin: { y: 0.3, x: 0.2 }, colors: ["#FFD300", "#FFE149"] }), 250);
              setTimeout(() => confetti({ particleCount: 40, spread: 100, origin: { y: 0.3, x: 0.8 }, colors: ["#FFD300", "#FFE149"] }), 500);
            } catch (err) {
              // Confetti is decorative — never block the success flow on animation errors.
              console.warn("Confetti animation failed; continuing.", err);
            }
          }
          // Fetch enrollments to show what was purchased
          try {
            const { data: enrolls } = await api.get("/enrollments");
            setEnrollments(enrolls);
          } catch (err) {
            // Purchase succeeded — enrollment list is a nice-to-have on this page.
            console.warn("Failed to fetch enrollments after checkout.", err);
          }
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts.current >= 8) { setStatus("timeout"); return; }
        attempts.current += 1;
        setTimeout(poll, 2000);
      } catch (err) {
        setStatus(err?.response?.status === 401 ? "signin" : "error");
      }
    };
    poll();
  }, [sessionId]);

  // Detect if any firearm/carry-related course was purchased
  const hasCarryCourse = enrollments.some((e) => {
    const cat = e.course?.category?.toLowerCase() || "";
    return cat.includes("wear") || cat.includes("carry") || cat.includes("officer") || cat.includes("protection") || cat.includes("civilian");
  });

  return (
    <div data-testid="checkout-success-page">
      <Seo title="Order Confirmed" path="/checkout/success" noindex />
      {/* ============== CONFIRMATION ============== */}
      <section className="min-h-[60vh] flex items-center pt-16 pb-12">
        <div className="container-x">
          <div className="max-w-3xl mx-auto">
            {status === "checking" && (
              <div className="surface-card p-12 text-center">
                <Clock size={64} weight="duotone" className="text-[#FFD300] mx-auto mb-6 animate-pulse" />
                <div className="overline mb-3">// PROCESSING</div>
                <h1 className="font-heading text-3xl mb-3">Verifying your payment...</h1>
                <p className="text-neutral-400">Please wait. Do not close this page.</p>
              </div>
            )}
            {status === "paid" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="surface-card p-12"
              >
                <div className="flex items-start gap-6 mb-8">
                  <div className="w-20 h-20 bg-[#039855]/15 flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={48} weight="duotone" className="text-[#039855]" />
                  </div>
                  <div className="flex-1">
                    <div className="overline mb-2 text-[#039855]">// PAYMENT CONFIRMED</div>
                    <h1 className="font-heading text-4xl md:text-5xl tracking-tighter mb-3">You're enrolled, operator.</h1>
                    <p className="text-neutral-300 text-lg">Confirmation sent. Your training credential is now active.</p>
                    {info && (
                      <div className="font-mono text-xs text-neutral-500 mt-3">
                        Receipt total: ${(info.amount_total/100).toFixed(2)} {info.currency?.toUpperCase()} · Session: {info.session_id?.slice(0, 24)}...
                      </div>
                    )}
                  </div>
                </div>

                {enrollments.length > 0 && (
                  <div className="border-t border-white/10 pt-6 mb-8" data-testid="purchased-courses">
                    <div className="overline mb-4">// COURSES ENROLLED</div>
                    <div className="space-y-3">
                      {enrollments.slice(-5).reverse().map((e) => (
                        <Link key={e.id} to={`/learn/${e.id}`} className="flex items-center justify-between p-4 bg-black border border-white/10 hover:border-[#FFD300]/50 transition-colors group">
                          <div>
                            <div className="font-heading text-base">{e.course.title}</div>
                            <div className="font-mono text-[11px] text-neutral-500 mt-1">
                              {e.course.code}
                              {e.class_session && ` · Class: ${new Date(e.class_session.starts_at).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}`}
                            </div>
                          </div>
                          <ArrowRight size={16} className="text-neutral-500 group-hover:text-[#FFD300] group-hover:translate-x-1 transition-all" />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10">
                  <Link to="/dashboard" className="btn-primary" data-testid="go-dashboard">Go to Dashboard <ArrowRight size={14} /></Link>
                  <Link to="/training" className="btn-secondary">Browse More Courses</Link>
                </div>
              </motion.div>
            )}
            {status === "signin" && (
              <div className="surface-card p-12 text-center" data-testid="checkout-signin-notice">
                <div className="overline mb-3">// PAYMENT RECEIVED</div>
                <h1 className="font-heading text-3xl mb-3">Sign in to see your confirmation</h1>
                <p className="text-neutral-400 mb-6">Your payment went through. Sign in to the account you enrolled with to view your seat and receipt.</p>
                <Link to={`/login?next=${encodeURIComponent(`/checkout/success?session_id=${sessionId}`)}`} className="btn-primary" data-testid="checkout-signin-btn">Sign in <ArrowRight size={14} /></Link>
              </div>
            )}
            {(status === "expired" || status === "timeout" || status === "error") && (
              <div className="surface-card p-12 text-center">
                <div className="overline mb-3 text-red-400">// {status.toUpperCase()}</div>
                <h1 className="font-heading text-3xl mb-3">We couldn't confirm your payment</h1>
                <p className="text-neutral-400 mb-6">If you were charged, please contact our team. Otherwise try again.</p>
                <Link to="/cart" className="btn-secondary">Back to Cart</Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ============== US LAWSHIELD HOOK (only on paid + firearm course) ============== */}
      {status === "paid" && (hasCarryCourse || enrollments.length === 0) && (
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="border-y border-[#FFD300]/30 bg-gradient-to-br from-[#1a1100] via-black to-[#0a0a0a] section-pad relative overflow-hidden"
          data-testid="post-purchase-lawshield"
        >
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FFD300] to-transparent" />
          <div className="container-x">
            <div className="max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                className="overline mb-4 text-[#FFD300] flex items-center gap-3"
              >
                <Lightning size={14} weight="fill" />
                CRITICAL NEXT STEP · BEFORE YOUR FIRST CARRY
              </motion.div>

              <h2 className="font-heading text-4xl md:text-6xl tracking-tighter leading-[0.95] mb-6">
                You've earned the credential.<br/>
                <span className="text-[#FFD300]">Now protect yourself if you ever have to use it.</span>
              </h2>

              <p className="text-neutral-300 text-lg leading-relaxed max-w-3xl mb-10">
                A self-defense incident — even a clean, justified one — costs <strong className="text-white">$30,000 to $150,000 in legal fees on average</strong>.
                That's why every SEP graduate is strongly encouraged to enroll with <strong className="text-white">U.S. LawShield®</strong>:
                the nation's largest legal-defense program for armed citizens.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 mb-10">
                {[
                  { Icon: Phone, title: "24/7 Attorney Hotline", desc: "Real lawyer answers — not a call center. Day one of an incident, you have counsel." },
                  { Icon: Shield, title: "No Caps on Legal Fees", desc: "Civil and criminal defense fully covered. No deductible. No cap on attorney hours." },
                  { Icon: CheckCircle, title: "Covered Across States", desc: "Protection that travels with you — including the jurisdictions we train in." },
                ].map((b, i) => (
                  <div key={i} className="bg-black p-6">
                    <b.Icon size={26} weight="duotone" className="text-[#FFD300] mb-3" />
                    <div className="font-heading text-lg tracking-tight mb-1">{b.title}</div>
                    <div className="text-neutral-400 text-sm leading-relaxed">{b.desc}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <a
                  href={US_LAWSHIELD_URL}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  className="block group"
                  data-testid="post-purchase-lawshield-banner"
                >
                  <div className="border-2 border-[#FFD300] group-hover:border-white transition-colors p-1 bg-white/5">
                    <img
                      src={US_LAWSHIELD_BANNER}
                      alt="Sign up for U.S. LawShield — 30 Days Free Trial"
                      width="468"
                      height="60"
                      className="w-full h-auto block"
                    />
                  </div>
                </a>

                <div>
                  <a
                    href={US_LAWSHIELD_URL}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="btn-primary text-base py-4 px-6 w-full justify-center"
                    data-testid="post-purchase-lawshield-cta"
                  >
                    Claim 30-Day Free Trial <ArrowSquareOut size={14} />
                  </a>
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500 mt-3 text-center md:text-left">
                    Opens in new tab · SEP-recommended partner · You can return to your dashboard at any time
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white/5 font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-600">
                SEP-recommended legal-defense partner · We recommend U.S. LawShield because we believe in it.
              </div>
            </div>
          </div>
        </motion.section>
      )}
    </div>
  );
}
